import { useState, useCallback, type JSX } from 'react';
import { ThemeContext } from './contexts';
import {
  type ThemePreferenceType,
  type ThemePreferenceValue,
  type ThemeSetterCallback,
} from '../../styles/types';
import { useStorageThemeListener } from '@/hooks/theme/useStorageThemeListener';
import { useSystemThemeListener } from '@/hooks/theme/useSystemThemeListener';
import { useSyncTheme } from '@/hooks/theme/useSyncTheme';
import { useThemePreferencesSync } from '@/hooks/theme/useThemePreferencesSync';
import { getSavedThemePreference } from '@/lib/theme';
import { THEME_PREFERENCES } from '@/styles/constants';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider = (props: ThemeProviderProps): JSX.Element => {
  // First-paint seed from localStorage, used until the preferences query resolves.
  const [fallbackTheme] = useState<ThemePreferenceType>(() => {
    return getSavedThemePreference();
  });
  const [themePreferenceValue, setThemePreferenceValue] = useState<
    ThemePreferenceValue | undefined
  >(() => {
    if (fallbackTheme !== THEME_PREFERENCES.SYSTEM) {
      return getSavedThemePreference() as ThemePreferenceValue;
    }
  });

  const savedPreferences = useThemePreferencesSync(fallbackTheme);
  const themePreference = savedPreferences.themePreference;

  const themeSetterCb: ThemeSetterCallback = useCallback(
    systemThemeValue => {
      setThemePreferenceValue(systemThemeValue);
    },
    [setThemePreferenceValue]
  );

  const updateThemePreference = useCallback(
    (newPreference: ThemePreferenceType) => {
      const isSystemPreference = newPreference === THEME_PREFERENCES.SYSTEM;
      if (!isSystemPreference) {
        setThemePreferenceValue(newPreference);
      }
      savedPreferences.sync.commit(newPreference);
    },
    [savedPreferences, setThemePreferenceValue]
  );

  const commitPreferenceFromStorage = useCallback(
    (next: ThemePreferenceType): void => {
      savedPreferences.commitToCache(next);
    },
    [savedPreferences]
  );

  useStorageThemeListener(commitPreferenceFromStorage, setThemePreferenceValue);
  useSystemThemeListener(themePreference, themePreferenceValue, themeSetterCb);
  // A concrete preference is its own value; only System needs the OS-derived one.
  // Without this, a Dark preference resolved from the API after a Light seed stays light.
  useSyncTheme(
    themePreference,
    themePreference === THEME_PREFERENCES.SYSTEM
      ? themePreferenceValue
      : (themePreference as ThemePreferenceValue)
  );

  return (
    <ThemeContext.Provider
      value={{
        value: themePreference,
        onChange: updateThemePreference,
        isPending: savedPreferences.sync.isPending,
        hasSyncError: savedPreferences.sync.hasError,
        retrySync: savedPreferences.sync.retry,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};
